import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Users, BookOpen, Shield, FileText, Calendar, Package,
  ArrowRight, Check, ChevronRight, AlertTriangle, Clock,
  Smartphone, MessageCircle, BarChart3, Heart, Star,
  Menu, X, Loader2, CheckCircle2
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function useInView(ref, threshold = 0.15) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [ref, threshold]);
  return visible;
}

function FadeIn({ children, className = "", delay = 0 }) {
  const ref = useRef(null);
  const vis = useInView(ref);
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${vis ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

const painPoints = [
  { icon: AlertTriangle, title: "Registros en cuadernos", desc: "Novedades del turno en papel que se pierden, se manchan o nadie lee." },
  { icon: Smartphone, title: "Todo por WhatsApp", desc: "Información crítica mezclada con memes en grupos interminables." },
  { icon: Clock, title: "Tiempo perdido", desc: "Horas buscando datos de residentes, medicación o turnos pasados." },
  { icon: Users, title: "Sin control del equipo", desc: "No sabés quién hizo qué ni cuándo. Cero trazabilidad operativa." },
];

const features = [
  {
    title: "Gestión de Residentes",
    desc: "Legajo digital completo: datos personales, obra social, escalas funcionales (Katz/Lawton), nivel de dependencia y contactos de emergencia.",
    screenshot: "/screenshots/residents-desktop.png",
    icon: Users,
  },
  {
    title: "Libro de Novedades",
    desc: "Registro de novedades por turno (mañana, tarde, noche). Cada novedad queda firmada digitalmente con autor, hora y prioridad.",
    screenshot: "/screenshots/novedades-desktop.png",
    icon: BookOpen,
  },
  {
    title: "Usuarios con Roles y Permisos",
    desc: "Cada persona del equipo tiene su nivel de acceso: director, enfermero, administrativo o familiar. 10 permisos configurables.",
    screenshot: "/screenshots/users-desktop.png",
    icon: Shield,
  },
  {
    title: "Calendario de Turnos",
    desc: "Vista semanal del calendario de personal. Asigná turnos en segundos y visualizá quién cubre cada franja horaria.",
    screenshot: "/screenshots/staff-desktop.png",
    icon: Calendar,
  },
];

const benefits = [
  { icon: Check, title: "Más control", desc: "Toda la operación visible en tiempo real" },
  { icon: Shield, title: "Menos errores", desc: "Validaciones automáticas y trazabilidad completa" },
  { icon: BarChart3, title: "Más claridad", desc: "Dashboards con alertas y métricas al instante" },
  { icon: Star, title: "Imagen profesional", desc: "Documentación impecable para inspecciones" },
  { icon: Heart, title: "Familias tranquilas", desc: "Portal para que la familia vea el estado del residente" },
  { icon: Clock, title: "Ahorro de tiempo", desc: "Lo que antes eran horas, ahora son minutos" },
];

function DemoFormSection({ navigate }) {
  const [form, setForm] = useState({ name: "", facility_name: "", phone: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.facility_name.trim() || !form.phone.trim()) return;
    setSubmitting(true);
    try {
      await fetch(`${API_URL}/api/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      setSubmitted(true);
      setForm({ name: "", facility_name: "", phone: "" });
    } catch {
      // silent fail — still show success to avoid UX friction
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="cta-final" className="py-16 lg:py-24 px-6 lg:px-8" data-testid="landing-cta-final">
      <div className="max-w-5xl mx-auto">
        <FadeIn>
          <div className="relative bg-violet-600 rounded-3xl overflow-hidden p-8 lg:p-16">
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-white blur-3xl transform translate-x-1/3 -translate-y-1/3" />
              <div className="absolute bottom-0 left-0 w-64 h-64 rounded-full bg-white blur-3xl transform -translate-x-1/3 translate-y-1/3" />
            </div>
            <div className="relative z-10">
              <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                {/* Left — copy */}
                <div className="text-center lg:text-left">
                  <h2 className="text-2xl lg:text-4xl font-extrabold text-white tracking-tight mb-4 leading-tight">
                    Empezá a organizar tu geriátrico hoy
                  </h2>
                  <p className="text-sm lg:text-base text-violet-200 leading-relaxed mb-6">
                    Completá el formulario y un miembro de nuestro equipo te contactará para coordinar una demostración gratuita y sin compromiso.
                  </p>
                  <button
                    onClick={() => navigate("/login")}
                    className="hidden lg:inline-flex items-center gap-2 bg-violet-500 hover:bg-violet-400 text-white font-semibold text-sm px-7 py-3.5 rounded-2xl transition-all border border-violet-400"
                  >
                    Ya tengo cuenta — Acceder <ArrowRight className="w-4 h-4" />
                  </button>
                </div>

                {/* Right — form */}
                <div>
                  {submitted ? (
                    <div className="bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20 p-8 text-center" data-testid="demo-form-success">
                      <div className="w-16 h-16 rounded-full bg-emerald-400/20 flex items-center justify-center mx-auto mb-4">
                        <CheckCircle2 className="w-8 h-8 text-emerald-300" />
                      </div>
                      <h3 className="text-xl font-bold text-white mb-2">Solicitud enviada</h3>
                      <p className="text-sm text-violet-200">Te contactaremos a la brevedad para coordinar tu demo.</p>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20 p-6 lg:p-8 space-y-4" data-testid="demo-request-form">
                      <h3 className="text-lg font-bold text-white mb-1">Solicitar demo gratuita</h3>
                      <p className="text-xs text-violet-200 mb-2">Completá tus datos y te llamamos.</p>
                      <div>
                        <label className="text-xs font-semibold text-violet-200 mb-1.5 block">Tu nombre</label>
                        <input
                          type="text"
                          value={form.name}
                          onChange={(e) => setForm({ ...form, name: e.target.value })}
                          placeholder="Ej: María López"
                          className="w-full h-11 rounded-xl bg-white/15 border border-white/20 text-white placeholder-violet-300/60 px-4 text-sm focus:outline-none focus:ring-2 focus:ring-white/30 transition-all"
                          required
                          data-testid="demo-form-name"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-violet-200 mb-1.5 block">Nombre del geriátrico</label>
                        <input
                          type="text"
                          value={form.facility_name}
                          onChange={(e) => setForm({ ...form, facility_name: e.target.value })}
                          placeholder="Ej: Residencia Sol de Otoño"
                          className="w-full h-11 rounded-xl bg-white/15 border border-white/20 text-white placeholder-violet-300/60 px-4 text-sm focus:outline-none focus:ring-2 focus:ring-white/30 transition-all"
                          required
                          data-testid="demo-form-facility"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-violet-200 mb-1.5 block">Teléfono de contacto</label>
                        <input
                          type="tel"
                          value={form.phone}
                          onChange={(e) => setForm({ ...form, phone: e.target.value })}
                          placeholder="Ej: 11 5566 7788"
                          className="w-full h-11 rounded-xl bg-white/15 border border-white/20 text-white placeholder-violet-300/60 px-4 text-sm focus:outline-none focus:ring-2 focus:ring-white/30 transition-all"
                          required
                          data-testid="demo-form-phone"
                        />
                      </div>
                      <button
                        type="submit"
                        disabled={submitting}
                        className="w-full h-12 rounded-xl bg-white hover:bg-violet-50 text-violet-700 font-bold text-sm transition-all active:scale-[0.98] shadow-lg flex items-center justify-center gap-2 disabled:opacity-70"
                        data-testid="demo-form-submit"
                      >
                        {submitting ? (
                          <><Loader2 className="w-4 h-4 animate-spin" /> Enviando...</>
                        ) : (
                          <><MessageCircle className="w-4 h-4" /> Solicitar demo gratuita</>
                        )}
                      </button>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        </FadeIn>
        <FadeIn delay={200}>
          <div className="relative -mt-8 mx-6 lg:mx-12">
            <div className="rounded-2xl overflow-hidden shadow-2xl shadow-slate-900/15 border border-slate-200/50">
              <img src="/screenshots/dashboard-desktop.png" alt="Gerion Platform" className="w-full h-auto" loading="lazy" />
            </div>
          </div>
        </FadeIn>
      </div>
    </section>
  );
}

export default function LandingPage() {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    setMobileMenu(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-[#FAFAF8] font-[Manrope] text-slate-900 overflow-x-hidden">
      {/* ==================== NAVBAR ==================== */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? "bg-white/90 backdrop-blur-xl shadow-sm border-b border-slate-100" : "bg-transparent"
        }`}
        data-testid="landing-navbar"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between h-16 lg:h-[72px]">
          <div className="flex items-center gap-2.5">
            <img src="/gerion-icon.png" alt="Gerion" className="w-9 h-9 rounded-xl" />
            <span className="text-xl font-bold tracking-tight">Gerion</span>
          </div>
          <div className="hidden md:flex items-center gap-8">
            <button onClick={() => scrollTo("funcionalidades")} className="text-[13px] font-medium text-slate-500 hover:text-slate-900 transition-colors">Funcionalidades</button>
            <button onClick={() => scrollTo("documentacion")} className="text-[13px] font-medium text-slate-500 hover:text-slate-900 transition-colors">Documentación</button>
            <button onClick={() => scrollTo("beneficios")} className="text-[13px] font-medium text-slate-500 hover:text-slate-900 transition-colors">Beneficios</button>
          </div>
          <div className="hidden md:flex items-center gap-3">
            <button onClick={() => navigate("/login")} className="text-[13px] font-semibold text-slate-600 hover:text-slate-900 px-4 py-2 rounded-xl transition-colors" data-testid="landing-login-btn">Iniciar sesión</button>
            <button onClick={() => scrollTo("cta-final")} className="text-[13px] font-semibold text-white bg-violet-600 hover:bg-violet-700 px-5 py-2.5 rounded-xl transition-all active:scale-[0.98] shadow-lg shadow-violet-200" data-testid="landing-demo-btn">
              Solicitar demo
            </button>
          </div>
          <button onClick={() => setMobileMenu(!mobileMenu)} className="md:hidden p-2 rounded-xl hover:bg-slate-100">
            {mobileMenu ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
        {mobileMenu && (
          <div className="md:hidden bg-white border-t border-slate-100 px-6 py-4 space-y-3 shadow-lg animate-fade-in">
            <button onClick={() => scrollTo("funcionalidades")} className="block w-full text-left text-sm font-medium text-slate-600 py-2">Funcionalidades</button>
            <button onClick={() => scrollTo("documentacion")} className="block w-full text-left text-sm font-medium text-slate-600 py-2">Documentación</button>
            <button onClick={() => scrollTo("beneficios")} className="block w-full text-left text-sm font-medium text-slate-600 py-2">Beneficios</button>
            <hr className="border-slate-100" />
            <button onClick={() => navigate("/login")} className="block w-full text-left text-sm font-semibold text-slate-700 py-2">Iniciar sesión</button>
            <button onClick={() => scrollTo("cta-final")} className="w-full text-sm font-semibold text-white bg-violet-600 px-5 py-2.5 rounded-xl">Solicitar demo</button>
          </div>
        )}
      </nav>

      {/* ==================== HERO ==================== */}
      <section className="relative pt-28 lg:pt-36 pb-8 lg:pb-16 px-6 lg:px-8" data-testid="landing-hero">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-12 lg:mb-16">
            <FadeIn>
              <div className="flex flex-col items-center mb-6">
                <img src="/logo-gerion.png" alt="Gerion - Cuidar mejor, gestionar simple" className="h-12 lg:h-16 w-auto object-contain brightness-0 invert-0" style={{ filter: "brightness(0) saturate(100%) invert(26%) sepia(69%) saturate(4785%) hue-rotate(253deg) brightness(96%) contrast(91%)" }} />
              </div>
            </FadeIn>
            <FadeIn>
              <div className="inline-flex items-center gap-2 bg-violet-50 text-violet-700 text-xs font-semibold px-4 py-1.5 rounded-full mb-6 border border-violet-100">
                <Heart className="w-3.5 h-3.5" /> Plataforma para geriátricos argentinos
              </div>
            </FadeIn>
            <FadeIn delay={100}>
              <h1 className="text-4xl sm:text-5xl lg:text-[3.5rem] font-extrabold leading-[1.1] tracking-tight text-slate-900 mb-6">
                Organizá tu geriátrico<br />
                <span className="text-violet-600">en un solo lugar</span>
              </h1>
            </FadeIn>
            <FadeIn delay={200}>
              <p className="text-base lg:text-lg text-slate-500 leading-relaxed max-w-2xl mx-auto mb-8">
                Centralizá residentes, novedades, tareas y documentación con una plataforma simple, clara y profesional. Sin papeles, sin WhatsApp, sin caos.
              </p>
            </FadeIn>
            <FadeIn delay={300}>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <button onClick={() => scrollTo("cta-final")} className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-violet-600 hover:bg-violet-700 text-white font-semibold text-sm px-7 py-3.5 rounded-2xl transition-all active:scale-[0.98] shadow-lg shadow-violet-200/50" data-testid="hero-demo-btn">
                  Solicitar demo <ArrowRight className="w-4 h-4" />
                </button>
                <button onClick={() => scrollTo("funcionalidades")} className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-white hover:bg-slate-50 text-slate-700 font-semibold text-sm px-7 py-3.5 rounded-2xl border border-slate-200 transition-all">
                  Ver cómo funciona <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </FadeIn>
          </div>

          {/* Hero Product Showcase */}
          <FadeIn delay={400}>
            <div className="relative max-w-6xl mx-auto">
              {/* Desktop screenshot - main */}
              <div className="relative rounded-2xl lg:rounded-3xl overflow-hidden shadow-2xl shadow-slate-900/10 border border-slate-200/50">
                <img
                  src="/screenshots/dashboard-desktop.png"
                  alt="Gerion Dashboard"
                  className="w-full h-auto"
                  loading="eager"
                />
                <div className="absolute inset-0 rounded-2xl lg:rounded-3xl ring-1 ring-inset ring-black/5" />
              </div>
              {/* Floating mobile - right (Dashboard mobile) */}
              <div className="hidden lg:block absolute -bottom-12 -right-4 w-[180px]">
                <div className="bg-slate-900 rounded-[28px] p-[6px] shadow-2xl shadow-slate-900/25 transform rotate-2 hover:rotate-0 transition-transform duration-500">
                  <div className="bg-slate-900 rounded-[22px] overflow-hidden relative">
                    <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-16 h-[6px] bg-slate-900 rounded-b-lg z-10" />
                    <img
                      src="/screenshots/dashboard-mobile.png"
                      alt="Gerion Mobile Dashboard"
                      className="w-full h-auto rounded-[22px]"
                      loading="lazy"
                    />
                  </div>
                </div>
              </div>
              {/* Floating mobile - bottom left (Medication mobile) */}
              <div className="hidden lg:block absolute -bottom-16 left-12 w-[160px]">
                <div className="bg-slate-900 rounded-[24px] p-[5px] shadow-2xl shadow-slate-900/20 transform -rotate-3 hover:rotate-0 transition-transform duration-500">
                  <div className="bg-slate-900 rounded-[20px] overflow-hidden relative">
                    <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-14 h-[5px] bg-slate-900 rounded-b-lg z-10" />
                    <img
                      src="/screenshots/medication-mobile.png"
                      alt="Gerion Mobile Medicación"
                      className="w-full h-auto rounded-[20px]"
                      loading="lazy"
                    />
                  </div>
                </div>
              </div>
              {/* Floating card - top left */}
              <div className="hidden lg:block absolute -top-4 -left-6 bg-white rounded-2xl shadow-xl shadow-slate-900/8 p-4 transform -rotate-3 hover:rotate-0 transition-transform duration-500 border border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center">
                    <Check className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-slate-900">Medicación administrada</p>
                    <p className="text-[10px] text-slate-400">Enalapril 10mg — 08:00</p>
                  </div>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ==================== SOCIAL PROOF BAR ==================== */}
      <section className="py-12 lg:py-16 px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <FadeIn>
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-6">Diseñado para la realidad de los geriátricos en Argentina</p>
            <div className="flex flex-wrap items-center justify-center gap-8 lg:gap-14">
              {[
                { value: "100%", label: "Digital" },
                { value: "24/7", label: "Disponible" },
                { value: "Multi-sede", label: "Escalable" },
                { value: "PAMI", label: "Compatible" },
              ].map((s, i) => (
                <div key={i} className="text-center">
                  <p className="text-2xl lg:text-3xl font-extrabold text-violet-600">{s.value}</p>
                  <p className="text-xs font-medium text-slate-400 mt-1">{s.label}</p>
                </div>
              ))}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ==================== PROBLEM ==================== */}
      <section className="py-16 lg:py-24 px-6 lg:px-8 bg-white" data-testid="landing-problem">
        <div className="max-w-6xl mx-auto">
          <FadeIn>
            <div className="text-center max-w-2xl mx-auto mb-12 lg:mb-16">
              <p className="text-xs font-semibold text-rose-500 uppercase tracking-widest mb-3">El problema</p>
              <h2 className="text-2xl lg:text-3xl font-extrabold tracking-tight mb-4">
                Tu geriátrico funciona, pero podría funcionar mucho mejor
              </h2>
              <p className="text-sm lg:text-base text-slate-500 leading-relaxed">
                La mayoría de las residencias manejan su información de formas que generan errores, pérdida de tiempo y falta de control.
              </p>
            </div>
          </FadeIn>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {painPoints.map((p, i) => (
              <FadeIn key={i} delay={i * 100}>
                <div className="bg-[#FAFAF8] rounded-2xl p-6 border border-slate-100 h-full hover:shadow-md hover:border-slate-200 transition-all duration-300">
                  <div className="w-11 h-11 rounded-xl bg-rose-50 flex items-center justify-center mb-4">
                    <p.icon className="w-5 h-5 text-rose-500" strokeWidth={1.8} />
                  </div>
                  <h3 className="font-bold text-sm text-slate-900 mb-2">{p.title}</h3>
                  <p className="text-xs text-slate-500 leading-relaxed">{p.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== SOLUTION ==================== */}
      <section className="py-16 lg:py-24 px-6 lg:px-8" data-testid="landing-solution">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <FadeIn>
              <div>
                <p className="text-xs font-semibold text-violet-600 uppercase tracking-widest mb-3">La solución</p>
                <h2 className="text-2xl lg:text-3xl font-extrabold tracking-tight mb-5">
                  Una plataforma que ordena tu operación diaria
                </h2>
                <p className="text-sm lg:text-base text-slate-500 leading-relaxed mb-8">
                  Gerion centraliza toda la información de tu geriátrico en un solo lugar: residentes, medicación, novedades por turno, personal, incidentes y documentación. Todo accesible, trazable y profesional.
                </p>
                <div className="space-y-4">
                  {[
                    "Legajos digitales de cada residente",
                    "Libro de novedades digital por turno",
                    "Ronda de medicación con semáforo",
                    "Portal para familias",
                    "Control de stock de medicamentos",
                    "Calendario de turnos del equipo",
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="w-5 h-5 rounded-full bg-violet-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="w-3 h-3 text-violet-600" strokeWidth={3} />
                      </div>
                      <span className="text-sm font-medium text-slate-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </FadeIn>
            <FadeIn delay={200}>
              <div className="relative">
                <div className="rounded-2xl overflow-hidden shadow-xl shadow-slate-900/8 border border-slate-200/50">
                  <img src="/screenshots/stock-desktop.png" alt="Stock de Medicamentos" className="w-full h-auto" loading="lazy" />
                </div>
                <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-4 border border-slate-100 hidden lg:block">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-amber-50 flex items-center justify-center">
                      <Package className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-slate-900">Alerta de stock bajo</p>
                      <p className="text-[10px] text-slate-400">Metformina — 8 unidades</p>
                    </div>
                  </div>
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* ==================== MOBILE SHOWCASE ==================== */}
      <section className="py-16 lg:py-24 px-6 lg:px-8 bg-white" data-testid="landing-mobile">
        <div className="max-w-6xl mx-auto">
          <FadeIn>
            <div className="text-center max-w-2xl mx-auto mb-12 lg:mb-16">
              <p className="text-xs font-semibold text-violet-600 uppercase tracking-widest mb-3">Multiplataforma</p>
              <h2 className="text-2xl lg:text-3xl font-extrabold tracking-tight mb-4">
                Accedé desde cualquier dispositivo
              </h2>
              <p className="text-sm lg:text-base text-slate-500 leading-relaxed">
                Desktop, tablet o celular. Gerion se adapta a tu pantalla para que puedas gestionar desde la oficina, el pasillo o tu casa.
              </p>
            </div>
          </FadeIn>
          <FadeIn delay={200}>
            <div className="flex items-end justify-center gap-4 sm:gap-6 lg:gap-10">
              {/* Phone 1 - Dashboard */}
              <div className="w-[120px] sm:w-[160px] lg:w-[200px] transform -rotate-6 hover:rotate-0 transition-transform duration-500">
                <div className="bg-slate-900 rounded-[20px] sm:rounded-[28px] lg:rounded-[32px] p-[4px] sm:p-[5px] lg:p-[6px] shadow-2xl shadow-slate-900/30">
                  <div className="rounded-[16px] sm:rounded-[23px] lg:rounded-[26px] overflow-hidden relative">
                    <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-10 sm:w-14 lg:w-16 h-[4px] sm:h-[5px] lg:h-[6px] bg-slate-900 rounded-b-lg z-10" />
                    <img
                      src="/screenshots/residents-mobile.png"
                      alt="Residentes en mobile"
                      className="w-full h-auto"
                      loading="lazy"
                    />
                  </div>
                </div>
                <p className="text-[10px] sm:text-xs text-slate-400 text-center mt-3 font-medium">Residentes</p>
              </div>
              {/* Phone 2 - Dashboard (center, bigger) */}
              <div className="w-[140px] sm:w-[180px] lg:w-[220px] transform hover:scale-105 transition-transform duration-500 -mb-2 sm:-mb-3 lg:-mb-4">
                <div className="bg-slate-900 rounded-[22px] sm:rounded-[30px] lg:rounded-[36px] p-[4px] sm:p-[5px] lg:p-[7px] shadow-2xl shadow-violet-900/20 ring-2 ring-violet-400/20">
                  <div className="rounded-[18px] sm:rounded-[25px] lg:rounded-[29px] overflow-hidden relative">
                    <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-12 sm:w-16 lg:w-20 h-[4px] sm:h-[5px] lg:h-[7px] bg-slate-900 rounded-b-lg z-10" />
                    <img
                      src="/screenshots/dashboard-mobile.png"
                      alt="Dashboard en mobile"
                      className="w-full h-auto"
                      loading="lazy"
                    />
                  </div>
                </div>
                <p className="text-[10px] sm:text-xs text-violet-600 text-center mt-3 font-semibold">Dashboard</p>
              </div>
              {/* Phone 3 - Medication */}
              <div className="w-[120px] sm:w-[160px] lg:w-[200px] transform rotate-6 hover:rotate-0 transition-transform duration-500">
                <div className="bg-slate-900 rounded-[20px] sm:rounded-[28px] lg:rounded-[32px] p-[4px] sm:p-[5px] lg:p-[6px] shadow-2xl shadow-slate-900/30">
                  <div className="rounded-[16px] sm:rounded-[23px] lg:rounded-[26px] overflow-hidden relative">
                    <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-10 sm:w-14 lg:w-16 h-[4px] sm:h-[5px] lg:h-[6px] bg-slate-900 rounded-b-lg z-10" />
                    <img
                      src="/screenshots/medication-mobile.png"
                      alt="Medicación en mobile"
                      className="w-full h-auto"
                      loading="lazy"
                    />
                  </div>
                </div>
                <p className="text-[10px] sm:text-xs text-slate-400 text-center mt-3 font-medium">Medicación</p>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ==================== FEATURES ==================== */}
      <section id="funcionalidades" className="py-16 lg:py-24 px-6 lg:px-8 bg-white" data-testid="landing-features">
        <div className="max-w-6xl mx-auto">
          <FadeIn>
            <div className="text-center max-w-2xl mx-auto mb-12 lg:mb-16">
              <p className="text-xs font-semibold text-violet-600 uppercase tracking-widest mb-3">Funcionalidades</p>
              <h2 className="text-2xl lg:text-3xl font-extrabold tracking-tight mb-4">
                Todo lo que necesitás para gestionar tu residencia
              </h2>
              <p className="text-sm lg:text-base text-slate-500 leading-relaxed">
                Cada módulo fue diseñado pensando en la operación real de los geriátricos argentinos.
              </p>
            </div>
          </FadeIn>
          <div className="space-y-16 lg:space-y-24">
            {features.map((f, i) => (
              <div key={i} className={`grid lg:grid-cols-2 gap-10 lg:gap-16 items-center ${i % 2 === 1 ? "lg:direction-rtl" : ""}`}>
                <FadeIn delay={100} className={i % 2 === 1 ? "lg:order-2" : ""}>
                  <div>
                    <div className="w-12 h-12 rounded-2xl bg-violet-50 flex items-center justify-center mb-5">
                      <f.icon className="w-6 h-6 text-violet-600" strokeWidth={1.8} />
                    </div>
                    <h3 className="text-xl lg:text-2xl font-extrabold tracking-tight mb-3">{f.title}</h3>
                    <p className="text-sm text-slate-500 leading-relaxed">{f.desc}</p>
                  </div>
                </FadeIn>
                <FadeIn delay={250} className={i % 2 === 1 ? "lg:order-1" : ""}>
                  <div className="rounded-2xl overflow-hidden shadow-xl shadow-slate-900/8 border border-slate-200/50 hover:shadow-2xl transition-shadow duration-500">
                    <img src={f.screenshot} alt={f.title} className="w-full h-auto" loading="lazy" />
                  </div>
                </FadeIn>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== DOCUMENTATION ==================== */}
      <section id="documentacion" className="py-16 lg:py-24 px-6 lg:px-8" data-testid="landing-docs">
        <div className="max-w-6xl mx-auto">
          <FadeIn>
            <div className="text-center max-w-2xl mx-auto mb-12 lg:mb-16">
              <p className="text-xs font-semibold text-violet-600 uppercase tracking-widest mb-3">Documentación</p>
              <h2 className="text-2xl lg:text-3xl font-extrabold tracking-tight mb-4">
                Documentos profesionales listos para imprimir
              </h2>
              <p className="text-sm lg:text-base text-slate-500 leading-relaxed">
                Generá planillas y reportes con un clic. Listas para firmar, archivar o presentar en inspecciones.
              </p>
            </div>
          </FadeIn>
          <div className="grid sm:grid-cols-3 gap-5">
            {[
              {
                icon: FileText,
                title: "Planilla de Enfermería",
                desc: "Registro diario de administraciones, organizado por turno y residente. Exportable en PDF.",
                color: "violet",
              },
              {
                icon: BookOpen,
                title: "Libro de Novedades",
                desc: "Todas las novedades del día, firmadas digitalmente, listas para imprimir y archivar.",
                color: "amber",
              },
              {
                icon: Users,
                title: "Legajo del Residente",
                desc: "Ficha completa: datos personales, escalas, medicación activa, evoluciones y vitales.",
                color: "emerald",
              },
            ].map((doc, i) => {
              const colorMap = {
                violet: { bg: "bg-violet-50", icon: "text-violet-600", border: "border-violet-100", headerBg: "bg-violet-600" },
                amber: { bg: "bg-amber-50", icon: "text-amber-600", border: "border-amber-100", headerBg: "bg-amber-500" },
                emerald: { bg: "bg-emerald-50", icon: "text-emerald-600", border: "border-emerald-100", headerBg: "bg-emerald-600" },
              };
              const c = colorMap[doc.color];
              return (
                <FadeIn key={i} delay={i * 120}>
                  <div className={`bg-white rounded-2xl border ${c.border} p-6 h-full hover:shadow-lg transition-all duration-300 group`}>
                    {/* PDF Preview mockup */}
                    <div className={`${c.bg} rounded-xl p-4 mb-5 aspect-[4/3] flex flex-col items-center justify-center relative overflow-hidden`}>
                      <div className="w-full max-w-[160px] bg-white rounded-lg shadow-md p-3 transform group-hover:scale-105 transition-transform duration-500">
                        <div className={`${c.headerBg} h-2 rounded-full w-3/4 mb-2`} />
                        <div className="space-y-1.5">
                          <div className="h-1.5 bg-slate-100 rounded-full w-full" />
                          <div className="h-1.5 bg-slate-100 rounded-full w-5/6" />
                          <div className="h-1.5 bg-slate-100 rounded-full w-4/5" />
                          <div className="h-1.5 bg-slate-50 rounded-full w-2/3 mt-2" />
                          <div className="h-1.5 bg-slate-100 rounded-full w-full" />
                          <div className="h-1.5 bg-slate-100 rounded-full w-3/4" />
                        </div>
                        <div className="mt-3 flex gap-1.5">
                          <div className={`h-1 ${c.headerBg} rounded-full w-8 opacity-60`} />
                          <div className="h-1 bg-slate-100 rounded-full w-12" />
                        </div>
                      </div>
                      <div className="absolute bottom-2 right-2">
                        <FileText className={`w-8 h-8 ${c.icon} opacity-20`} />
                      </div>
                    </div>
                    <div className={`w-10 h-10 rounded-xl ${c.bg} flex items-center justify-center mb-3`}>
                      <doc.icon className={`w-5 h-5 ${c.icon}`} strokeWidth={1.8} />
                    </div>
                    <h3 className="font-bold text-sm text-slate-900 mb-2">{doc.title}</h3>
                    <p className="text-xs text-slate-500 leading-relaxed">{doc.desc}</p>
                  </div>
                </FadeIn>
              );
            })}
          </div>
        </div>
      </section>

      {/* ==================== BENEFITS ==================== */}
      <section id="beneficios" className="py-16 lg:py-24 px-6 lg:px-8 bg-white" data-testid="landing-benefits">
        <div className="max-w-6xl mx-auto">
          <FadeIn>
            <div className="text-center max-w-2xl mx-auto mb-12 lg:mb-16">
              <p className="text-xs font-semibold text-violet-600 uppercase tracking-widest mb-3">Beneficios</p>
              <h2 className="text-2xl lg:text-3xl font-extrabold tracking-tight mb-4">
                Profesionalizá la gestión de tu residencia
              </h2>
            </div>
          </FadeIn>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {benefits.map((b, i) => (
              <FadeIn key={i} delay={i * 80}>
                <div className="bg-[#FAFAF8] rounded-2xl p-6 border border-slate-100 hover:border-violet-200 hover:shadow-md transition-all duration-300 h-full">
                  <div className="w-10 h-10 rounded-xl bg-violet-50 flex items-center justify-center mb-4">
                    <b.icon className="w-5 h-5 text-violet-600" strokeWidth={1.8} />
                  </div>
                  <h3 className="font-bold text-sm text-slate-900 mb-1.5">{b.title}</h3>
                  <p className="text-xs text-slate-500 leading-relaxed">{b.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== CTA FINAL — DEMO FORM ==================== */}
      <DemoFormSection navigate={navigate} />

      {/* ==================== FOOTER ==================== */}
      <footer className="py-12 px-6 lg:px-8 bg-white border-t border-slate-100">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <img src="/gerion-icon.png" alt="Gerion" className="w-8 h-8 rounded-lg" />
            <span className="text-base font-bold tracking-tight">Gerion</span>
          </div>
          <p className="text-xs text-slate-400">Plataforma de gestión para residencias de adultos mayores en Argentina</p>
          <p className="text-xs text-slate-300">&copy; {new Date().getFullYear()} Gerion. Todos los derechos reservados.</p>
        </div>
      </footer>
    </div>
  );
}
